import React, { useMemo } from 'react';
import { Row, Col, Card, Typography, Tag, Space, Alert, Anchor, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DashboardOutlined, CloudUploadOutlined, ClearOutlined,
  BranchesOutlined, TeamOutlined, FileTextOutlined, ClockCircleOutlined,
  TrophyOutlined, FileSearchOutlined, ShopOutlined,
  DatabaseOutlined, SwapOutlined, ThunderboltOutlined, LineChartOutlined,
  DollarOutlined, BulbOutlined, FilePdfOutlined, WalletOutlined,
  GlobalOutlined, CheckCircleOutlined,
  ShoppingCartOutlined, SettingOutlined, UserOutlined, ApartmentOutlined,
  SafetyCertificateOutlined, AuditOutlined, BookOutlined, SearchOutlined,
  ImportOutlined, CreditCardOutlined,
} from '@ant-design/icons';
import { usePermissions } from '../contexts/PermissionContext';
import '../styles/Phase5.css';

const { Title, Paragraph } = Typography;

// ---------------------------------------------------------------------------
// Feature illustration placeholder
// ---------------------------------------------------------------------------
const FeatureIllustration = ({ icon, color, label }) => (
  <div
    style={{
      background: `linear-gradient(135deg, ${color}15, ${color}08)`,
      border: `1px dashed ${color}40`,
      borderRadius: 8,
      padding: '16px 12px',
      textAlign: 'center',
    }}
  >
    <div style={{ fontSize: 28, color, marginBottom: 4 }}>{icon}</div>
    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, lineHeight: 1.3 }}>{label}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Static guide content — mirrors sidebar groups and features
// ---------------------------------------------------------------------------
const GUIDE_CONTENT = [
  {
    key: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardOutlined />,
    features: [
      {
        key: 'dashboard-overview',
        title: 'Dashboard Overview',
        icon: <DashboardOutlined />,
        route: '/',
        permission: ['dashboard', 'view'],
        description:
          'Your central command center for vendor management. See key metrics at a glance including total spend, active vendors, tracked products, potential savings, and recommendation progress.',
        capabilities: [
          'View total annual spend across all vendors',
          'Monitor active vendor count and products tracked',
          'Track potential and implemented savings',
          'Review recommendation progress and review rates',
          'See top vendors by spend at a glance',
          'Get alerts on contracts expiring within 90 days',
        ],
        tips: [
          'Click any KPI card to navigate directly to the detailed view',
          'The savings capture rate shows how much of identified savings you have realized',
        ],
        illustrationIcon: <DashboardOutlined />,
        illustrationColor: '#1890ff',
        illustrationLabel: 'KPI Cards, Savings Progress, Top Vendors',
      },
    ],
  },
  {
    key: 'data-import',
    title: 'Data Import',
    icon: <ImportOutlined />,
    features: [
      {
        key: 'spend-import',
        title: 'Spend Import',
        icon: <CloudUploadOutlined />,
        route: '/spend-import',
        permission: ['spend', 'import'],
        description:
          'Upload your vendor spending data from CSV or Excel files. The import wizard guides you through column mapping, data preview, and validation before committing records.',
        capabilities: [
          'Upload CSV and Excel spend files',
          'Preview data before importing',
          'Map columns to required fields (vendor name, amount, date, category)',
          'View import history and manage previous uploads',
          'Delete imports and optionally remove associated records',
        ],
        tips: [
          'Ensure your file has vendor name, amount, and date columns at minimum',
          'Use the preview step to verify column mappings before committing the import',
        ],
        illustrationIcon: <CloudUploadOutlined />,
        illustrationColor: '#52c41a',
        illustrationLabel: 'File Upload, Column Mapping, Import History',
      },
      {
        key: 'data-cleansing',
        title: 'Data Cleansing',
        icon: <ClearOutlined />,
        route: '/data-cleansing',
        permission: ['data_cleansing', 'view'],
        description:
          'Clean and standardize your vendor data to ensure accuracy. Detect duplicate vendor entries, merge them into canonical records, and create aliases for name variations.',
        capabilities: [
          'Detect duplicate vendor entries with configurable similarity thresholds',
          'Merge duplicate groups into a canonical vendor record',
          'Create and manage supplier aliases for name variations',
          'View and standardize spending categories',
        ],
        tips: [
          'Run duplicate detection after every new spend import',
          'Creating aliases prevents future duplicates from the same vendor name variant',
        ],
        illustrationIcon: <ClearOutlined />,
        illustrationColor: '#faad14',
        illustrationLabel: 'Duplicate Detection, Merge Groups, Alias Management',
      },
      {
        key: 'vendor-mapping',
        title: 'Vendor Mapping',
        icon: <BranchesOutlined />,
        route: '/vendor-mapping',
        permission: ['vendor_mapping', 'view'],
        description:
          'Manage global vendor name standardization with ML-powered matching. Review pending matches, approve or reject them, and configure auto-resolution thresholds.',
        capabilities: [
          'Review pending vendor name matches with ML confidence scores',
          'Approve, reject, or create new vendors from unresolved names',
          'Manage global aliases across the organization',
          'Configure matching thresholds and auto-resolution settings',
          'Bulk approve or reject pending items',
        ],
        tips: [
          'Items with 95%+ confidence can often be auto-resolved safely',
          'Review the suggested matches carefully before bulk approving',
        ],
        illustrationIcon: <BranchesOutlined />,
        illustrationColor: '#722ed1',
        illustrationLabel: 'Pending Queue, Global Aliases, ML Matching',
      },
    ],
  },
  {
    key: 'my-vendors',
    title: 'My Vendors',
    icon: <TeamOutlined />,
    features: [
      {
        key: 'vendor-list',
        title: 'Vendor List',
        icon: <TeamOutlined />,
        route: '/my-vendors',
        permission: ['vendors', 'view'],
        description:
          "View and manage your organization's active vendor relationships. Track each vendor's status, category, spend, and contract details from a single table.",
        capabilities: [
          'See all vendors with status, category, spend, and contract info',
          'Add new vendors to your portfolio from the global database',
          'Track contract expiration dates and renewal status',
          'View vendor recommendations and product details',
          'Filter by category, status, or contract expiration',
        ],
        tips: [
          'Use the "expiring" filter to find contracts needing attention',
          'Click a vendor row to see its full detail page with products and recommendations',
        ],
        illustrationIcon: <TeamOutlined />,
        illustrationColor: '#1890ff',
        illustrationLabel: 'Vendor Table, Status Tracking, Quick Actions',
      },
      {
        key: 'contracts',
        title: 'Contracts',
        icon: <FileTextOutlined />,
        route: '/contracts',
        permission: ['contracts', 'view'],
        description:
          'Centralized contract repository with document management. Store contracts, track values and dates, and use AI to extract metadata from uploaded documents.',
        capabilities: [
          'Store and organize all vendor contracts',
          'Track contract values, start/end dates, and auto-renewal terms',
          'Upload contract documents (PDF, DOCX)',
          'AI-powered metadata extraction from uploaded documents',
          'View contract hierarchy (MSAs and sub-contracts)',
          'Filter by status, vendor, value, or expiration',
        ],
        tips: [
          'Upload a PDF and use "Extract Metadata" to auto-fill contract details',
          'Link sub-contracts to their parent MSA for organized tracking',
        ],
        illustrationIcon: <FileTextOutlined />,
        illustrationColor: '#13c2c2',
        illustrationLabel: 'Contract List, Upload & Extract, Hierarchy View',
      },
      {
        key: 'renewal-pipeline',
        title: 'Renewal Pipeline',
        icon: <ClockCircleOutlined />,
        route: '/renewals',
        permission: ['contracts', 'view'],
        description:
          'Track upcoming contract renewals in a timeline view. See which contracts are expiring soon and the total renewal value at risk.',
        capabilities: [
          'See contracts expiring in the next 30, 60, and 90 days',
          'View renewal pipeline by month',
          'Track total renewal value at risk',
          'Navigate directly to contract details from the pipeline',
        ],
        tips: [
          'Check the pipeline regularly to avoid auto-renewal surprises',
          'Contracts with auto-renewal clauses need extra attention before the deadline',
        ],
        illustrationIcon: <ClockCircleOutlined />,
        illustrationColor: '#fa8c16',
        illustrationLabel: 'Timeline View, Expiration Alerts, Renewal Tracking',
      },
      {
        key: 'scorecards',
        title: 'Vendor Scorecards',
        icon: <TrophyOutlined />,
        route: '/scorecards',
        permission: ['scorecards', 'view'],
        description:
          'Rate and track vendor performance across multiple dimensions. Configure scoring weights, view rankings, and compare vendors on quality, delivery, pricing, support, and innovation.',
        capabilities: [
          'Score vendors on quality, delivery, pricing, support, and innovation',
          'Configure scoring weights to match your priorities',
          'View vendor rankings and performance trends over time',
          'Compare vendors side-by-side on performance metrics',
          'Track historical scorecard trends by vendor',
        ],
        tips: [
          'Adjust scoring weights under the Weights tab to reflect your priorities',
          'Regular scoring (quarterly) gives the best trend data',
        ],
        illustrationIcon: <TrophyOutlined />,
        illustrationColor: '#fadb14',
        illustrationLabel: 'Score Cards, Rankings, Trend Charts',
      },
      {
        key: 'rfp-evaluation',
        title: 'RFP Evaluation',
        icon: <FileSearchOutlined />,
        route: '/rfp',
        permission: ['vendors', 'view'],
        description:
          'Structured vendor evaluation with a multi-step wizard. Create RFP/RFI/Vendor Selection projects, extract requirements from documents, score vendors, and select winners.',
        capabilities: [
          'Create RFP/RFI/Vendor Selection projects with configurable scoring scales',
          'Upload Business Requirements Documents (BRD) for AI extraction',
          'AI-powered requirement extraction from uploaded documents',
          'Add vendor candidates manually or via AI auto-match suggestions',
          'Score vendors against requirements with a scoring matrix (number or star input)',
          'Invite external vendors to respond via secure invitation links',
          'View aggregated results, rankings, and export to CSV',
        ],
        tips: [
          'Upload a BRD document to auto-extract requirements instead of entering them manually',
          'Use AI Suggestions on the Vendors step to discover matching vendors in your database',
          'Switch to number input mode on the scoring step for faster keyboard-driven scoring',
        ],
        illustrationIcon: <FileSearchOutlined />,
        illustrationColor: '#2f54eb',
        illustrationLabel: 'Projects, Requirements, Scoring Matrix, Vendor Portal',
      },
    ],
  },
  {
    key: 'discover',
    title: 'Discover',
    icon: <SearchOutlined />,
    features: [
      {
        key: 'all-vendors',
        title: 'All Vendors (Global Database)',
        icon: <ShopOutlined />,
        route: '/vendors',
        permission: ['vendors', 'view'],
        description:
          'Browse the global vendor database with full product catalogs. Search vendors, view details, and add them to your portfolio.',
        capabilities: [
          'Search and browse all vendors in the system',
          'View vendor details, websites, and product listings',
          "Add vendors to your organization's portfolio",
          'Trigger product data scraping for a vendor',
        ],
        tips: [
          'Use the search bar to quickly find vendors by name or category',
          'Click "Sync" on a vendor to refresh its product data from the web',
        ],
        illustrationIcon: <ShopOutlined />,
        illustrationColor: '#1890ff',
        illustrationLabel: 'Vendor Search, Detail View, Product Catalog',
      },
      {
        key: 'product-database',
        title: 'Product Database',
        icon: <DatabaseOutlined />,
        route: '/product-database',
        permission: ['vendors', 'view'],
        description:
          'View all scraped products with structured feature information. Filter by vendor, category, or keywords to find products and compare across vendors.',
        capabilities: [
          'Browse all products extracted from vendor websites',
          'Filter by vendor, category, or feature keywords',
          'View extracted features, pricing tiers, and descriptions',
          'See which products overlap across vendors',
        ],
        tips: [
          'Product data is extracted by AI from vendor websites and may need verification',
          'Use the category filter to compare similar products across vendors',
        ],
        illustrationIcon: <DatabaseOutlined />,
        illustrationColor: '#722ed1',
        illustrationLabel: 'Product Grid, Feature Details, Category Filters',
      },
      {
        key: 'compare-vendors',
        title: 'Compare Vendors',
        icon: <SwapOutlined />,
        route: '/compare',
        permission: ['vendors', 'view'],
        description:
          'Side-by-side vendor comparison on features and capabilities. Select vendors, see overlap, and identify consolidation opportunities.',
        capabilities: [
          'Select two or more vendors to compare',
          'See feature overlap and unique capabilities',
          'Compare pricing, categories, and product counts',
          'Identify consolidation opportunities',
        ],
        tips: [
          'Compare vendors in the same category for the most meaningful results',
          'Use the overlap score to gauge consolidation potential',
        ],
        illustrationIcon: <SwapOutlined />,
        illustrationColor: '#13c2c2',
        illustrationLabel: 'Side-by-Side View, Feature Matrix, Overlap Score',
      },
      {
        key: 'alternatives',
        title: 'Alternatives',
        icon: <ThunderboltOutlined />,
        route: '/alternatives',
        permission: ['recommendations', 'view'],
        description:
          'AI-generated alternative vendor recommendations based on product similarity. See potential savings from switching and review similarity scores.',
        capabilities: [
          'View recommended alternative vendors based on product similarity',
          'See potential savings from switching vendors',
          'Filter alternatives by category or status',
          'Review and act on recommendations',
        ],
        tips: [
          'Alternatives are generated by AI analysis of product feature similarity',
          'Review the similarity score to gauge how close a match the alternative is',
        ],
        illustrationIcon: <ThunderboltOutlined />,
        illustrationColor: '#eb2f96',
        illustrationLabel: 'Alternatives List, Savings Estimates, Similarity Scores',
      },
    ],
  },
  {
    key: 'analytics',
    title: 'Analytics',
    icon: <LineChartOutlined />,
    features: [
      {
        key: 'spend-analytics',
        title: 'Spend Analytics',
        icon: <DollarOutlined />,
        route: '/spend',
        permission: ['spend', 'view'],
        description:
          'Comprehensive spend visibility with interactive charts. Break down spending by vendor, category, and time period with pie, bar, line, and treemap visualizations.',
        capabilities: [
          'View total spend with month-over-month and year-over-year trends',
          'Break down spending by vendor, category, and time period',
          'Interactive charts: pie, bar, line, and treemap visualizations',
          'Filter by category, date range, or custom period',
          'Drill down into individual vendor spend details',
        ],
        tips: [
          'Use the time period selector to focus on specific quarters or years',
          'Click on chart segments to drill down into vendor-level detail',
        ],
        illustrationIcon: <DollarOutlined />,
        illustrationColor: '#52c41a',
        illustrationLabel: 'Spend Charts, Category Breakdown, Trend Lines',
      },
      {
        key: 'consolidation',
        title: 'Consolidation',
        icon: <BulbOutlined />,
        route: '/recommendations',
        permission: ['recommendations', 'view'],
        description:
          'AI-identified vendor consolidation opportunities. See which vendor pairs have overlapping products and estimate savings from consolidation.',
        capabilities: [
          'View vendor pairs with overlapping product capabilities',
          'See product-level overlap details and similarity scores',
          'Track consolidation status: review, approved, in-progress, completed',
          'Estimate savings from each consolidation opportunity',
          'Drill into detailed comparison of overlapping products',
        ],
        tips: [
          'Focus on vendor pairs with the highest overlap counts first',
          'Mark consolidations as "in progress" to track active initiatives',
        ],
        illustrationIcon: <BulbOutlined />,
        illustrationColor: '#faad14',
        illustrationLabel: 'Overlap Matrix, Savings Estimates, Status Tracking',
      },
      {
        key: 'reports',
        title: 'Reports',
        icon: <FilePdfOutlined />,
        route: '/reports',
        permission: ['reports', 'view'],
        description:
          'Generate and download formatted reports. Choose from pre-built templates, preview data, and export in PDF or Excel formats.',
        capabilities: [
          'Choose from pre-built report templates (spend summary, vendor analysis, etc.)',
          'Preview report data before downloading',
          'Export reports in PDF and Excel formats',
          'Customize report parameters and filters',
        ],
        tips: [
          'Use the preview to verify data before generating the full report',
          'Excel format is best for further analysis, PDF for presentations',
        ],
        illustrationIcon: <FilePdfOutlined />,
        illustrationColor: '#f5222d',
        illustrationLabel: 'Report Templates, Preview, PDF/Excel Export',
      },
      {
        key: 'forecasting',
        title: 'Forecasting',
        icon: <LineChartOutlined />,
        route: '/forecasting',
        permission: ['spend', 'view'],
        description:
          'Predictive spend forecasting with anomaly detection. Train models on historical data, view projections, and receive budget recommendations.',
        capabilities: [
          'Train forecasting models on your historical spend data',
          'View spend forecasts for upcoming months',
          'Detect spending anomalies that deviate from predictions',
          'Get AI-powered budget recommendations',
          'Review model accuracy metrics',
        ],
        tips: [
          'More historical data produces better forecasts \u2014 aim for 12+ months',
          'Review flagged anomalies promptly; they may indicate billing errors',
        ],
        illustrationIcon: <LineChartOutlined />,
        illustrationColor: '#1890ff',
        illustrationLabel: 'Forecast Charts, Anomaly Alerts, Budget Recommendations',
      },
      {
        key: 'cost-centers',
        title: 'Cost Centers',
        icon: <WalletOutlined />,
        route: '/cost-centers',
        permission: ['spend', 'view'],
        description:
          'Organize and track spending by organizational cost centers. Create hierarchical structures, assign budgets, and monitor actual vs. planned spend.',
        capabilities: [
          'Create hierarchical cost center structures',
          'Assign budgets and track actual vs. planned spend',
          'View cost center performance with variance analysis',
          'Drill down into cost center spend by vendor',
        ],
        tips: [
          'Set up cost centers to mirror your accounting structure',
          'Monitor budget utilization regularly to avoid overruns',
        ],
        illustrationIcon: <WalletOutlined />,
        illustrationColor: '#722ed1',
        illustrationLabel: 'Cost Center Tree, Budget vs. Actual, Variance Charts',
      },
      {
        key: 'market-intelligence',
        title: 'Market Intelligence',
        icon: <GlobalOutlined />,
        route: '/market-intelligence',
        permission: ['vendors', 'view'],
        description:
          'Vendor risk assessment and market pricing benchmarks. View risk profiles, compare your pricing against market data, and track data sources.',
        capabilities: [
          'View vendor risk profiles with overall risk scores',
          'Assess financial stability, operational, and strategic risks',
          'Compare your pricing against market benchmarks',
          'Track market data from configurable data sources',
          'Run price benchmarking analyses',
        ],
        tips: [
          'High-risk vendors need more frequent review and contingency planning',
          'Use pricing benchmarks in contract negotiations',
        ],
        illustrationIcon: <GlobalOutlined />,
        illustrationColor: '#eb2f96',
        illustrationLabel: 'Risk Gauges, Price Benchmarks, Data Sources',
      },
      {
        key: 'supplier-diversity',
        title: 'Supplier Diversity',
        icon: <TeamOutlined />,
        route: '/supplier-diversity',
        permission: ['vendors', 'view'],
        description:
          'Track and manage supplier diversity goals and certifications. Monitor spend breakdown by certification type and verify certifications.',
        capabilities: [
          'View diversity dashboard with spend breakdown by certification type',
          'Track progress against diversity goals',
          'Manage vendor diversity certifications (MBE, WBE, SDVOB, etc.)',
          'Verify certifications and track expiration dates',
          'View diversity spend trends over time',
        ],
        tips: [
          'Set annual diversity goals aligned with your corporate objectives',
          'Verify certifications before reporting to avoid compliance issues',
        ],
        illustrationIcon: <TeamOutlined />,
        illustrationColor: '#13c2c2',
        illustrationLabel: 'Diversity Dashboard, Goals, Certification Tracking',
      },
      {
        key: 'savings-levers',
        title: 'Savings Levers',
        icon: <DollarOutlined />,
        route: '/savings',
        permission: ['spend', 'view'],
        description:
          'Identify and track savings opportunities across multiple categories. The system automatically scans for payment term arbitrage, price variance, and volume consolidation opportunities.',
        capabilities: [
          'Scan for savings opportunities across all vendor relationships',
          'View opportunities by lever type: payment terms, price variance, volume consolidation',
          'Track opportunity status and implementation progress',
          'Estimate total addressable savings',
          'Prioritize opportunities by potential impact and confidence',
        ],
        tips: [
          'Run a full scan periodically to discover new opportunities',
          'Focus on high-confidence, high-impact opportunities first',
        ],
        illustrationIcon: <DollarOutlined />,
        illustrationColor: '#52c41a',
        illustrationLabel: 'Opportunity Table, Category Summary, Savings Pipeline',
      },
      {
        key: 'tasks',
        title: 'Tasks',
        icon: <CheckCircleOutlined />,
        route: '/tasks',
        permission: ['recommendations', 'view'],
        description:
          'Task management for vendor optimization initiatives. Create tasks from recommendations, assign to team members, and track progress.',
        capabilities: [
          'Create tasks from recommendations, contracts, or manually',
          'Assign tasks to team members with due dates and priorities',
          'Track task progress in list or Kanban board views',
          'Add comments and collaborate on tasks',
          'Bulk update tasks and filter by status or assignee',
        ],
        tips: [
          'Use the Kanban view for a visual workflow overview',
          'Create tasks directly from recommendations to maintain traceability',
        ],
        illustrationIcon: <CheckCircleOutlined />,
        illustrationColor: '#faad14',
        illustrationLabel: 'Task List, Kanban Board, Comments, Activity Feed',
      },
    ],
  },
  {
    key: 'accounts-payable',
    title: 'Accounts Payable',
    icon: <CreditCardOutlined />,
    features: [
      {
        key: 'purchase-orders',
        title: 'Purchase Orders',
        icon: <ShoppingCartOutlined />,
        route: '/purchase-orders',
        permission: ['invoices', 'view'],
        description:
          'Create and manage purchase orders with approval workflows. Draft POs with line items, submit for approval, and track status through receiving.',
        capabilities: [
          'Create purchase orders with line items',
          'Submit POs for approval through configurable workflows',
          'Track PO status: draft, submitted, approved, received',
          'Record goods/services receipt against POs',
          'View PO statistics and spending summaries',
        ],
        tips: [
          'Draft POs first, then submit for approval when ready',
          'Record receipts to enable three-way invoice matching',
        ],
        illustrationIcon: <ShoppingCartOutlined />,
        illustrationColor: '#1890ff',
        illustrationLabel: 'PO List, Line Items, Approval Flow, Receipt Tracking',
      },
      {
        key: 'invoices',
        title: 'Invoices',
        icon: <FileTextOutlined />,
        route: '/invoices',
        permission: ['invoices', 'view'],
        description:
          'Invoice processing with AI extraction and three-way matching. Upload invoices, auto-extract metadata, match against POs, and track payments.',
        capabilities: [
          'Upload invoices with AI-powered metadata extraction',
          'Three-way matching: invoice vs. PO vs. goods receipt',
          'Approve or reject invoices with notes',
          'Record payments and track payment history',
          'View aging reports and invoice statistics',
        ],
        tips: [
          'Upload a PDF invoice to auto-extract vendor, amount, and date',
          'Three-way matching helps catch billing errors before payment',
        ],
        illustrationIcon: <FileTextOutlined />,
        illustrationColor: '#52c41a',
        illustrationLabel: 'Invoice Upload, Three-Way Match, Aging Report, Payments',
      },
    ],
  },
  {
    key: 'admin',
    title: 'Administration',
    icon: <SettingOutlined />,
    features: [
      {
        key: 'approvals',
        title: 'Approvals',
        icon: <CheckCircleOutlined />,
        route: '/approvals',
        permission: ['approvals', 'view'],
        roles: ['admin', 'manager', 'finance'],
        description:
          'Manage approval workflows and process pending requests. Configure which actions require approval and at what thresholds.',
        capabilities: [
          'View pending approval requests with urgency indicators',
          'Approve or reject requests with notes',
          'Configure approval workflows for different entity types',
          'Set approval thresholds and required approver roles',
          'Track approval history and statistics',
        ],
        tips: [
          'Check pending approvals regularly to avoid bottlenecks',
          'Set up workflows for contracts and POs that exceed value thresholds',
        ],
        illustrationIcon: <CheckCircleOutlined />,
        illustrationColor: '#52c41a',
        illustrationLabel: 'Pending Queue, Workflow Configuration, Approval History',
      },
      {
        key: 'users',
        title: 'User Management',
        icon: <UserOutlined />,
        route: '/users',
        permission: ['users', 'view'],
        adminOnly: true,
        description:
          'Manage user accounts, roles, and department assignments. Create users, assign appropriate roles, and control access across the platform.',
        capabilities: [
          'Create, edit, and deactivate user accounts',
          'Assign roles: admin, manager, analyst, finance, viewer',
          'Manage department memberships',
          'Reset passwords and manage account status',
        ],
        tips: [
          'Follow the principle of least privilege when assigning roles',
          'Deactivate accounts instead of deleting to preserve audit trails',
        ],
        illustrationIcon: <UserOutlined />,
        illustrationColor: '#1890ff',
        illustrationLabel: 'User Table, Role Assignment, Department Membership',
      },
      {
        key: 'departments',
        title: 'Departments',
        icon: <ApartmentOutlined />,
        route: '/departments',
        permission: ['departments', 'edit'],
        adminOnly: true,
        description:
          'Configure organizational departments and team structures. Set department heads, assign members, and manage budgets.',
        capabilities: [
          'Create and manage departments',
          'Set department heads and assign members',
          'Manage department budgets and cost centers',
          'View department-level spend breakdowns',
        ],
        tips: [
          'Align departments with your org chart for accurate spend attribution',
          'Assign department heads to enable approval routing',
        ],
        illustrationIcon: <ApartmentOutlined />,
        illustrationColor: '#722ed1',
        illustrationLabel: 'Department List, Members, Budget Tracking',
      },
      {
        key: 'organization-settings',
        title: 'Organization Settings',
        icon: <SettingOutlined />,
        route: '/settings',
        permission: ['organization', 'view'],
        adminOnly: true,
        description:
          'Configure your organization profile, permission matrix, and system-wide settings. Manage role-based permissions and approval workflows.',
        capabilities: [
          'Update organization name, industry, and contact information',
          'Customize the role-permission matrix for your org',
          'View organization statistics',
          'Configure approval workflow rules',
        ],
        tips: [
          'Keep your industry setting accurate; it affects benchmarking comparisons',
          'Review the permission matrix after adding new roles',
        ],
        illustrationIcon: <SettingOutlined />,
        illustrationColor: '#faad14',
        illustrationLabel: 'Org Profile, Permission Matrix, Configuration',
      },
      {
        key: 'security',
        title: 'Security',
        icon: <SafetyCertificateOutlined />,
        route: '/security',
        // No permission check — visible to all authenticated users
        description:
          'Personal security settings including multi-factor authentication and session management. Admins can also configure SSO and SCIM provisioning.',
        capabilities: [
          'Enable/disable multi-factor authentication (TOTP)',
          'View and revoke active sessions',
          'Manage recovery codes for MFA',
          'Configure SSO (Single Sign-On) settings (admin only)',
          'Set up SCIM provisioning for automated user management (admin only)',
        ],
        tips: [
          'Enable MFA for an extra layer of account security',
          'Revoke unfamiliar sessions immediately if you suspect unauthorized access',
        ],
        illustrationIcon: <SafetyCertificateOutlined />,
        illustrationColor: '#f5222d',
        illustrationLabel: 'MFA Setup, Session Management, SSO Config',
      },
      {
        key: 'audit-logs',
        title: 'Audit Logs',
        icon: <AuditOutlined />,
        route: '/audit',
        permission: ['audit', 'view'],
        adminOnly: true,
        description:
          'View a complete audit trail of all system actions. Filter by event type, user, and date. Export logs for compliance reporting.',
        capabilities: [
          'Browse audit logs with filtering by event type, user, and date',
          'View detailed event information and metadata',
          'Export audit logs in CSV or JSON format',
          'Monitor system activity statistics and trends',
        ],
        tips: [
          'Export audit logs regularly for compliance record-keeping',
          'Filter by event type to investigate specific activity patterns',
        ],
        illustrationIcon: <AuditOutlined />,
        illustrationColor: '#8c8c8c',
        illustrationLabel: 'Log Table, Filters, Export, Activity Stats',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const UserGuide = () => {
  const navigate = useNavigate();
  const { can, isAdmin, hasAnyRole, user } = usePermissions();

  // Filter content based on the current user's permissions
  const filteredContent = useMemo(() => {
    return GUIDE_CONTENT.map((group) => {
      const visibleFeatures = group.features.filter((feature) => {
        if (feature.adminOnly && !isAdmin()) return false;
        if (feature.roles && !hasAnyRole(feature.roles)) return false;
        if (feature.permission) {
          const [resource, action] = feature.permission;
          return can(resource, action);
        }
        return true;
      });
      return { ...group, features: visibleFeatures };
    }).filter((group) => group.features.length > 0);
  }, [can, isAdmin, hasAnyRole]);

  const roleName = user?.role || 'user';

  return (
    <div className="p5-page">
      <div className="p5-page-header">
        <h1 className="p5-page-title">
          <BookOutlined style={{ marginRight: 10 }} />
          User Guide
        </h1>
        <p className="p5-page-subtitle">
          Learn how to use every feature of the vendor optimization platform.
          This guide is personalized to show the features available to your role.
        </p>
        <Tag color="blue" style={{ marginTop: 8, fontSize: 13 }}>
          {roleName.charAt(0).toUpperCase() + roleName.slice(1)} Role
        </Tag>
      </div>

      <Row gutter={32} style={{ marginTop: 24 }}>
        {/* Table of Contents */}
        <Col xs={0} sm={0} md={6} lg={5} xl={4}>
          <div style={{ position: 'sticky', top: 80, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
            <Anchor
              affix={false}
              items={filteredContent.map((group) => ({
                key: group.key,
                href: `#${group.key}`,
                title: (
                  <Space size={6}>
                    {group.icon}
                    <span>{group.title}</span>
                  </Space>
                ),
                children: group.features.map((feature) => ({
                  key: feature.key,
                  href: `#${feature.key}`,
                  title: feature.title,
                })),
              }))}
            />
          </div>
        </Col>

        {/* Content */}
        <Col xs={24} sm={24} md={18} lg={19} xl={20}>
          {filteredContent.map((group) => (
            <div key={group.key} id={group.key} style={{ marginBottom: 48 }}>
              <div className="p5-section-title">
                {group.icon} {group.title}
              </div>

              {group.features.map((feature) => (
                <Card
                  key={feature.key}
                  id={feature.key}
                  style={{ marginBottom: 24 }}
                  title={
                    <Space>
                      {feature.icon}
                      <span>{feature.title}</span>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(feature.route)}
                      >
                        Go to page
                      </Button>
                    </Space>
                  }
                >
                  <Row gutter={16}>
                    <Col xs={24} md={5}>
                      <FeatureIllustration
                        icon={feature.illustrationIcon}
                        color={feature.illustrationColor}
                        label={feature.illustrationLabel}
                      />
                    </Col>
                    <Col xs={24} md={19}>
                      <Paragraph>{feature.description}</Paragraph>

                      <Title level={5}>Key Capabilities</Title>
                      <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                        {feature.capabilities.map((cap, i) => (
                          <li key={i} style={{ marginBottom: 4 }}>
                            {cap}
                          </li>
                        ))}
                      </ul>

                      {feature.tips && feature.tips.length > 0 && (
                        <Alert
                          type="info"
                          showIcon
                          icon={<BulbOutlined />}
                          message="Tips"
                          description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {feature.tips.map((tip, i) => (
                                <li key={i}>{tip}</li>
                              ))}
                            </ul>
                          }
                        />
                      )}
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          ))}
        </Col>
      </Row>
    </div>
  );
};

export default UserGuide;
